import * as Xstate from "@xstate/react";
import React, { useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import { LottieLoading } from "../../components/graphics/LottieLoading";
import { PageLayout } from "../../components/layouts/PageLayout";
import { NavigationTabs } from "../../components/navigationTabs/NavigationTabs";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { getUser } from "../../services/AppService";
import { GetPageTitle } from "../../support/ScrollToTop";
import { NoAccessBody } from "../customFields/CustomFieldModals";
import { PlanAndSeatsPage } from "../subscription/PlanAndSeatsPage";
import { useSubscriptionStatus } from "../subscription/useSubscription";
import {
  settingsTabs,
  SUBSCRIPTION_PATH,
  VIEW_SETTINGS_PERMISSION,
} from "./utils/constants";
import { SETTINGS_ROUTES } from "./utils/routes";
import { renderRoutes } from "./utils/utils";
import "./settingsType.css";

export const SettingsView: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const { user, isLoadingUser } = useAppSelector((state) => state.appReducer);
  // Settings > Subscription (ProCharted's own plan): only people with "Manage
  // subscription & payment", also without Settings access. The old address
  // Settings > Billing > Plan & seats moves there.
  const { pathname } = useLocation();
  const { status: billing, isLoading: isLoadingBilling } =
    useSubscriptionStatus();
  const canManagePlan = !!billing?.seatBilling && !!billing.canManage;
  const isSubscriptionPage = /\/settings\/subscription\/?$/.test(pathname);
  const isOldPlanPage = /\/settings\/billing\/plan\/?$/.test(pathname);

  useEffect(() => {
    document.title = GetPageTitle("Settings");
  }, []);

  useEffect(() => {
    dispatch(
      getUser({
        userId: `${userInfoByEmail?.id}`,
        orgId: `${userInfoByEmail?.org?.id}`,
      }),
    );
  }, [dispatch, userInfoByEmail?.id, userInfoByEmail?.org?.id]);

  if (isLoadingUser) {
    return <LottieLoading />;
  }

  if (isOldPlanPage) {
    return <Navigate to={SUBSCRIPTION_PATH} replace={true} />;
  }

  return (
    <PageLayout
      title={"Settings"}
      titleClasses={
        "mb-4 text-[26px] font-extrabold leading-[32px] text-[#1F2433]"
      }>
      <NavigationTabs tabs={settingsTabs(canManagePlan)} />
      {/* Home / Work page type scale inside Settings: settingsType.css */}
      <div className={"proc-settings"}>
        {isSubscriptionPage ? (
          canManagePlan ? (
            <PlanAndSeatsPage />
          ) : isLoadingBilling ? (
            <LottieLoading />
          ) : (
            <NoAccessPanel onClose={() => navigate("/home")} />
          )
        ) : (user.permissionIds &&
            user.permissionIds.includes(VIEW_SETTINGS_PERMISSION)) ||
          user?.userRoleEnum?.userRole === "Admin" ? (
          <Routes>
            <Route
              index
              element={<Navigate to={"./company"} replace={true} />}
            />
            {renderRoutes(SETTINGS_ROUTES)}
          </Routes>
        ) : (
          <NoAccessPanel onClose={() => navigate("/home")} />
        )}
      </div>
    </PageLayout>
  );
};

const NoAccessPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className={"flex h-[70vh] items-center justify-center p-6"}>
    <div
      role={"alertdialog"}
      aria-label={"You don’t have access"}
      className={
        "w-full max-w-[400px] rounded-2xl border border-[#E8E8EC] bg-white px-[26px] pb-[22px] pt-[26px] text-center shadow-[0_24px_60px_rgba(20,22,34,0.12)]"
      }
    >
      <NoAccessBody onClose={onClose} />
    </div>
  </div>
);
